import {Injectable} from '@angular/core';
import {distinctUntilChanged, filter, map, skip, take, takeUntil, tap} from 'rxjs/operators';
import {BehaviorSubject, forkJoin, Observable, Subject, timer} from 'rxjs';
import {LabelingService} from '../shared/labeling.service';
import {PermissionService} from './permission.service';
import {IPermission} from './permission';
import {IUser} from '../users/users';
import {SecureLocalService} from './secure-local.service';
import {CommunicationService} from '../viba/agents/live/agent-live/communication.service';
import {Location} from '@angular/common';
import {OperatorFunction} from 'rxjs/internal/types';
import {MIHttpClientService} from '../shared/mi-http-client.service';
import {IAuthenticationToken} from './authentication';
import {sortCaseInsensitive} from '../shared/custom-rxjs-operators';
import {VibaQueueService} from '../viba/agents/service/viba-queue.service';
import {ShowMenuInitializerService} from '../shared/show-menu-initializer.service';
import {IOrganizationData} from "./organization";
import {LogoService} from "./navbar/logo.service";
import {ConfigurationService} from "./configuration.service";
import {Router} from "@angular/router";
import {LookerEmbedService} from '../reporting/looker-dashboards/looker-embed.service';


@Injectable({providedIn: 'root'})
export class AuthenticationService {

  constructor(
    private miClient: MIHttpClientService,
    private labelingService: LabelingService,
    private configurationService: ConfigurationService,
    private router: Router,
    private permissionService: PermissionService,
    private logoService: LogoService,
    private secureService: SecureLocalService,
    private communicationService: CommunicationService,
    public vibaQueueService: VibaQueueService,
    private location: Location,
    private showMenuInitializerService: ShowMenuInitializerService,
    private lookerEmbedService: LookerEmbedService,
  ) {
    this.activeOrganizationSubject = new BehaviorSubject<any>(+localStorage.getItem('selectedOrganization'));
    this.activeOrganizationInfo$ = this.activeOrganizationSubject.asObservable()
      .pipe(filter(org => !!org));

    this.organizationsSubject = new Subject<any>();
    this.organizationsInfo$ = this.organizationsSubject.asObservable();

    this.activeLanguageSubject = new Subject<any>();
    this.languageInfo$ = this.activeLanguageSubject.asObservable();

    const selectedOrg = localStorage.getItem('selectedOrganization');
    if (selectedOrg) {
      queueMicrotask(() => this.lookerEmbedService.warmCookielessSession(selectedOrg));
    }
  }

  public isLoggedIn = false;
  public isEmbeddedAgent = false;
  public isAuthInitialized = false;
  public organizations = [];
  public selectedOrganization: number;
  public currentUser: IUser = JSON.parse(localStorage.getItem('currentUser'));
  organizationChangeDisabled = false;
  public activeOrganizationInfo$: Observable<any>;
  public organizationsInfo$: Observable<any>;
  private activeOrganizationSubject: Subject<any>;
  private organizationsSubject: Subject<any>;

  public activeLanguageSubject: Subject<any>;
  public languageInfo$: Observable<any>;

  logoutEvent = new Subject<any>();
  changeOrganizationEvent = new Subject<any>();

  organizationDataUpdatedEvent = new Subject<void>();

  get organizationData(): IOrganizationData {
    return JSON.parse(localStorage.getItem('organizationData')) || {canUpdatePermissions: false, logoUrl: null, missingLogo: true};
  }

  hookListActionOnOrgChange(action: any, untilDestroyed$?: Observable<void>): void {
    this.activeOrganizationInfo$
      .pipe(
        skip(1),
        distinctUntilChanged(),
        this.takeUntilIfPresent(untilDestroyed$),
      )
      .subscribe(() => {
        // clear url
        const parts = this.router.url.split('?');
        this.location.replaceState(parts[0]);
        action();
      });
  }

  setActiveOrganization(value): Observable<IPermission[]> {
    this.selectedOrganization = value;
    localStorage.setItem('selectedOrganization', value);

    const canUpdatePermissions$ = this.permissionService.canUpdatePermissions();
    const logoUrl$ = this.logoService.getUrl(value);
    forkJoin([canUpdatePermissions$, logoUrl$]).subscribe(([permission, logo]) => {
      const orgData : IOrganizationData = {
        canUpdatePermissions: permission,
        logoUrl: logo,
        missingLogo: false
      };
      localStorage.setItem('organizationData', JSON.stringify(orgData));
      this.organizationDataUpdatedEvent.next();
    });

    this.activeOrganizationSubject.next(value);
    this.labelingService.orgChanged(value);
    this.configurationService.orgChanged(value);
    this.showMenuInitializerService.showMenu = true;
    this.lookerEmbedService.warmCookielessSession(String(value));

    return this.permissionService.getPermissions(value);
  }

  authenticate(username: string, password: string): Observable<IAuthenticationToken> {
    return this.miClient.postNoOrg('authenticate', {username, password}, false)
      .pipe(
        tap(authenticationToken => {
          if (authenticationToken) {
            const userProfile = authenticationToken.userProfile;
            userProfile.userDisplayName = userProfile.lastName + ', ' + userProfile.firstName + ' (' + username + ')';
            userProfile.organizations =  sortCaseInsensitive(userProfile.organizations, 'orgName');
            userProfile.userName = username;
          }
        }),
      );
  }

  login(username: string, password: string): Observable<any> {
    this.clearSecuredProps();

    return this.authenticate(username, password)
      .pipe(
        map(authenticationToken => {
          return this.setSessionParams(authenticationToken ,false);
        }),
      );
  }

  public setSessionParams(authenticationToken , isEmbeddedAgent?) {
    if (authenticationToken) {
      this.isEmbeddedAgent = isEmbeddedAgent;
      localStorage.setItem('authToken', authenticationToken.token);

      this.selectedOrganization = null;
      // clear old org from the subjects
      this.activeOrganizationSubject.next(0);

      this.currentUser = authenticationToken.userProfile;
      localStorage.setItem('currentUser', JSON.stringify(this.currentUser));

      this.organizations = authenticationToken.userProfile.organizations;
      this.organizationsSubject.next(this.organizations);

      this.organizationChangeDisabled = this.organizations.length === 1 || this.currentUser.isVibaUser;

      if (this.organizations.length === 1 || this.currentUser.isVibaUser) {
        // for users with single organization we need to store it and avoid org selection
        // for multi org viba users we need an org id to check permissions
        this.setActiveOrganization(this.organizations[0].id).subscribe();

        if (this.currentUser.isVibaUser) {
          this.loadAgentTypeAndData(this.organizations);
        }
      }

    }
    return authenticationToken?.userProfile;
  }

  private loadAgentTypeAndData(organizations) {
    this.secureService.setItem('vibaUserOrganizations', organizations.map(org => org.id));
    console.log('INIT Web RTC client');
    this.secureWebRTCAgentData(this.isEmbeddedAgent);
  }

  clearSecuredProps() {
    this.secureService.removeItem('web-rtc-agent-data');
    this.secureService.removeItem('vibaUserOrganizations');
  }

  secureWebRTCAgentData(hasBackgroundAgent) {
    this.miClient.get('agentData').subscribe((response) => {
      if (response != null) {
        this.secureService.setItem('web-rtc-agent-data', response);
        this.communicationService.videoCallInit();
        if(!hasBackgroundAgent) {
          this.vibaQueueService.agentLogin(response);
        }
      }
    });
  }

  public isNotAnAgent() {
    return this.secureService.isNotAnAgent();
  }

  public showNavigation() {
    return true;
  }

  clearLoginState() {
    this.isLoggedIn = false;
    this.isEmbeddedAgent = false;
    // Don't reset isAuthInitialized here - it should be managed separately
    this.selectedOrganization = null;
    this.permissionService.clearCache();
    localStorage.removeItem('currentUser');
    localStorage.removeItem('authToken');
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('selectedOrganization');
    localStorage.removeItem('labels');
    localStorage.removeItem('server');
    localStorage.removeItem('timezones');
    localStorage.removeItem('activeVisit');
    localStorage.removeItem('projectId');
    this.secureService.removeItem('agentData');
    this.secureService.removeItem('vibaUserOrganizations');

    this.showMenuInitializerService.showMenu = false;

    this.activeOrganizationSubject.next(null);
    this.lookerEmbedService.clearCookielessSession();
  }

  markAuthAsInitialized() {
    this.isAuthInitialized = true;
  }

  logout() {
    const currentUser: IUser = JSON.parse(localStorage.getItem('currentUser'));
    this.permissionService.hasPermission('VIBA_USER').pipe(take(1)).subscribe(
      hasPermission => {
        if (hasPermission) {
          this.vibaQueueService.agentLogout(currentUser.userId);
          this.communicationService.logout();
          timer(1000).subscribe(() => {
              this.clearLoginState();
              this.isAuthInitialized = true;
              return this.router.navigate(['login']);
            },
          );
        } else {
          // For non-VIBA users, logout immediately
          this.clearLoginState();
          this.isAuthInitialized = true;
          return this.router.navigate(['login']);
        }
      },
    );
  }

  takeUntilIfPresent<T>(until: Observable<any>): OperatorFunction<T, T> {
    return function (source: Observable<T>): Observable<T> {
      return until ? source.pipe(takeUntil(until)) : source;
    };
  }
}



